/**
 * Browser-side helpers to manage Web Push subscriptions.
 *
 * The flow:
 *   1. supportsPush() — quick capability check before showing UI.
 *   2. getPermissionStatus() — read current notification permission.
 *   3. subscribeToPush() — request permission + register with the
 *      push service + persist the subscription in `push_subscriptions`.
 *   4. unsubscribeFromPush() — un-register and delete the row.
 *
 * The VAPID public key lives in VITE_VAPID_PUBLIC_KEY (set in .env or
 * Vercel env). It's safe to ship to the client — that's its job.
 */

import { supabase } from '@/integrations/supabase/client';

export const VAPID_PUBLIC_KEY =
  (import.meta.env.VITE_VAPID_PUBLIC_KEY as string)?.trim()
  // Fallback to baked-in public key so deploys without env vars still
  // work. This is the public half — no security risk.
  || 'BNyC5M4iILxO_xmjY8YpGyaOcBrGrfsRfEiCd4os_emQQOD6juisQhXJT4ACdg6SDuYlY0HRTiwAFBb3nklHDRg';

export function supportsPush(): boolean {
  return (
    typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
  );
}

/** True when running as installed PWA (added-to-home-screen). iOS Safari
 *  ONLY allows web push when installed this way (iOS 16.4+). */
export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (navigator as any).standalone === true;
}

/** Detect iOS specifically — push there has unique constraints. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
}

/** Detect Android. */
export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

/** Plataforma detectada. Sirve para mostrar instrucciones específicas
 *  cuando el cliente ya rechazó las notificaciones y tiene que
 *  activarlas manualmente desde la configuración del sistema. */
export type Platform = 'ios' | 'android' | 'desktop';
export function detectPlatform(): Platform {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'desktop';
}

export type PermissionState = 'default' | 'granted' | 'denied';

export function getPermissionStatus(): PermissionState {
  if (!supportsPush()) return 'denied';
  return Notification.permission as PermissionState;
}

/** Convert the URL-safe base64 VAPID key into a Uint8Array for PushManager. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}

/**
 * Wait until the SW is registered and ready. Returns the registration.
 */
async function getRegistration(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register('/sw.js', { type: 'classic' }).catch(() => {
    // already registered by vite-plugin-pwa — fine
  });
  return navigator.serviceWorker.ready;
}

/**
 * Idempotent: requests permission if not granted, subscribes via the
 * SW push manager, and persists in Supabase. Safe to call from any
 * UI button without checking previous state.
 *
 * Returns true on success, false otherwise. Throws on hard errors so
 * the caller can show a meaningful toast.
 */
export async function subscribeToPush(): Promise<boolean> {
  if (!supportsPush()) throw new Error('Tu navegador no soporta notificaciones push.');

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new Error(perm === 'denied'
      ? 'Notificaciones bloqueadas. Ve a configuración de tu navegador para reactivarlas.'
      : 'Permiso de notificaciones cancelado.');
  }

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const json = sub.toJSON() as any;
  const endpoint = json.endpoint as string;
  const p256dh = json.keys?.p256dh as string;
  const auth = json.keys?.auth as string;
  if (!endpoint || !p256dh || !auth) {
    throw new Error('Suscripción inválida — intenta de nuevo.');
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sesión expirada — vuelve a iniciar sesión.');

  const userAgent = navigator.userAgent;

  // Tomar ownership del endpoint vía RPC SECURITY DEFINER.
  //
  // Por qué un RPC y no upsert directo: en dispositivos compartidos
  // (mismo Chrome, varias cuentas distintas), el browser genera el
  // MISMO endpoint para todas. Si otra cuenta ya lo registró antes,
  // el upsert directo choca con la RLS "user_id = auth.uid()" porque
  // el row pertenece a OTRO user. El RPC SECURITY DEFINER reescribe
  // el row reasignándolo al caller — es seguro porque el caller solo
  // puede claim un endpoint que su browser REALMENTE posee (Push API
  // no permite "robar" endpoints ajenos: cada browser tiene el suyo).
  //
  // El RPC también hace el dedup por (user_id, user_agent) que antes
  // se hacía client-side, en una sola transacción atómica.
  const { error } = await supabase.rpc('claim_push_subscription', {
    p_endpoint:   endpoint,
    p_p256dh:     p256dh,
    p_auth:       auth,
    p_user_agent: userAgent,
  });
  if (error) throw new Error('No se pudo guardar la suscripción: ' + error.message);

  return true;
}

/**
 * Remove this device's subscription both server-side and locally.
 */
export async function unsubscribeFromPush(): Promise<void> {
  if (!supportsPush()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;

  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});

  await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
}

/**
 * Whether THIS device + THIS user currently has an active push subscription.
 *
 * Verificación doble:
 *   1) Browser PushManager tiene una subscription activa
 *   2) Esa subscription está registrada en DB BAJO la cuenta actual
 *
 * Sin el (2), un usuario que se suscribió en este browser bajo OTRA
 * cuenta (ej. cuenta cliente jovalo290781@gmail.com) verá "ya suscrito"
 * cuando se logue como admin (jovalo81@hotmail.com), pero los pushes
 * no le llegan porque la row de DB tiene el user_id del cliente.
 * Solución: si el browser sub no coincide con una row de la cuenta
 * actual, retornar false → la UI re-muestra el botón de activar →
 * subscribeToPush() crea la row faltante con el user_id correcto.
 */
export async function isCurrentlySubscribed(): Promise<boolean> {
  if (!supportsPush()) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return false;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data } = await supabase
      .from('push_subscriptions')
      .select('endpoint')
      .eq('user_id', user.id)
      .eq('endpoint', sub.endpoint)
      .maybeSingle();

    return !!data;
  } catch {
    return false;
  }
}
